// =============================================================================
// 📁 File: src/App.tsx
// =============================================================================
// CTO Recovery Prompt — Canonical Tenant-Aware Routing Surface (FINAL)
//
// Invariants:
// - All protected routes are tenantSlug-prefixed
// - SidebarLayout NEVER redirects
// - ProtectedRoute is the ONLY auth gate
// - No hard redirects to /default/login
// - Wildcard + root are tenant-safe via FallbackRedirect
// - Legacy non-tenant role paths must NOT be mis-parsed as tenantSlug
// - Route ordering must prevent role segments (ddo/pm/finance/management)
//   from being consumed as tenantSlug by "/:tenantSlug/login"
// - Submission detail route must remain tenant-scoped + role-protected
// =============================================================================

import React from "react";
import { Routes, Route } from "react-router-dom";

import Login from "./pages/Login";
import DDODashboard from "./pages/ddo/DDODashboard";
import DDOQueuePage from "./pages/ddo/DDOQueue";
import DDOSubmissionReviewPage from "./pages/ddo/DDOSubmissionReviewPage";
import DDOExceptionQueue from "./pages/ddo/DDOExceptionQueue";

import PMDashboard from "./pages/pm/PMDashboard";
import PMExceptionQueue from "./pages/pm/PMExceptionQueue";
import PMFormAuthoringWorkspace from "./pages/pm/PMFormAuthoringWorkspace";
import PCDashboard from "./pages/pc/PCDashboard";
import PCExceptionQueue from "./pages/pc/PCExceptionQueue";
import FinanceDashboard from "./pages/finance/FinanceDashboard";
import FinanceExceptionQueue from "./pages/finance/FinanceExceptionQueue";
import ManagementDashboard from "./pages/management/ManagementDashboard";
import AdminOperationalLanding from "./pages/admin/AdminOperationalLanding";
import TenantSettingsPage from "./pages/admin/TenantSettingsPage";
import ApplicantLogin from "./pages/applicant/ApplicantLogin";
import ApplicantDashboard from "./pages/applicant/ApplicantDashboard";
import ClientDashboard from "./pages/client/ClientDashboard";
import OperationsOverview from "./pages/operations/OperationsOverview";
import Forbidden from "./pages/Forbidden";

import SidebarLayout from "./components/SidebarLayout";
import ProtectedRoute from "./components/ProtectedRoute";
import FallbackRedirect from "./components/FallbackRedirect";

const App: React.FC = () => {
  return (
    <Routes>
      {/* 🚫 Legacy non-tenant URL guards (MUST come BEFORE "/:tenantSlug/login") */}
      <Route path="/ddo/login" element={<FallbackRedirect replace />} />
      <Route path="/ddo/*" element={<FallbackRedirect replace />} />
      <Route path="/pm/*" element={<FallbackRedirect replace />} />
      <Route path="/pc/*" element={<FallbackRedirect replace />} />
      <Route path="/finance/*" element={<FallbackRedirect replace />} />
      <Route path="/management/*" element={<FallbackRedirect replace />} />
      <Route path="/admin/*" element={<FallbackRedirect replace />} />
      <Route path="/applicant/*" element={<FallbackRedirect replace />} />
      <Route path="/client/*" element={<FallbackRedirect replace />} />

      {/* 🔓 Public login (tenant-scoped) */}
      <Route path="/:tenantSlug/login" element={<Login />} />
      <Route path="/:tenantSlug/applicant/login" element={<ApplicantLogin />} />

      <Route
        path="/operations/overview"
        element={
          <ProtectedRoute allowedRoles={["ddo", "pm", "pc"]}>
            <OperationsOverview />
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/applicant/dashboard"
        element={
          <ProtectedRoute allowedRoles={["applicant"]}>
            <ApplicantDashboard />
          </ProtectedRoute>
        }
      />

      {/* 🔐 DDO Dashboard */}
      <Route
        path="/:tenantSlug/ddo/dashboard"
        element={
          <ProtectedRoute allowedRoles={["ddo"]}>
            <SidebarLayout>
              <DDODashboard />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🔐 DDO Queue */}
      <Route
        path="/:tenantSlug/ddo/submissions"
        element={
          <ProtectedRoute allowedRoles={["ddo"]}>
            <SidebarLayout>
              <DDOQueuePage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🔐 DDO Submission Detail (NEW — must come AFTER queue route) */}
      <Route
        path="/:tenantSlug/ddo/submissions/:submissionId"
        element={
          <ProtectedRoute allowedRoles={["ddo"]}>
            <SidebarLayout>
              <DDOSubmissionReviewPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* DDO governance exception queue */}
      <Route
        path="/:tenantSlug/ddo/exceptions"
        element={
          <ProtectedRoute allowedRoles={["ddo"]}>
            <SidebarLayout>
              <DDOExceptionQueue />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🔐 PM */}
      <Route
        path="/:tenantSlug/pm/dashboard"
        element={
          <ProtectedRoute allowedRoles={["pm"]}>
            <SidebarLayout>
              <PMDashboard />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/pm/exceptions"
        element={
          <ProtectedRoute allowedRoles={["pm"]}>
            <SidebarLayout>
              <PMExceptionQueue />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/pm/forms"
        element={
          <ProtectedRoute allowedRoles={["pm"]}>
            <SidebarLayout>
              <PMFormAuthoringWorkspace />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/pc/dashboard"
        element={
          <ProtectedRoute allowedRoles={["pc"]}>
            <SidebarLayout>
              <PCDashboard />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/pc/exceptions"
        element={
          <ProtectedRoute allowedRoles={["pc"]}>
            <SidebarLayout>
              <PCExceptionQueue />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🔐 Finance */}
      <Route
        path="/:tenantSlug/finance/dashboard"
        element={
          <ProtectedRoute allowedRoles={["finance"]}>
            <SidebarLayout>
              <FinanceDashboard />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/finance/exceptions"
        element={
          <ProtectedRoute allowedRoles={["finance"]}>
            <SidebarLayout>
              <FinanceExceptionQueue />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🔐 Management */}
      <Route
        path="/:tenantSlug/management/dashboard"
        element={
          <ProtectedRoute allowedRoles={["management"]}>
            <SidebarLayout>
              <ManagementDashboard />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/admin/dashboard"
        element={
          <ProtectedRoute allowedRoles={["admin"]}>
            <SidebarLayout>
              <AdminOperationalLanding />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/:tenantSlug/client/dashboard"
        element={
          <ProtectedRoute allowedRoles={["client"]}>
            <SidebarLayout>
              <ClientDashboard />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🔐 Super Admin */}
      <Route
        path="/:tenantSlug/admin/tenant-settings"
        element={
          <ProtectedRoute allowedRoles={["super_admin"]}>
            <SidebarLayout>
              <TenantSettingsPage />
            </SidebarLayout>
          </ProtectedRoute>
        }
      />

      {/* 🚫 Forbidden */}
      <Route path="/:tenantSlug/403" element={<Forbidden />} />

      {/* 🏠 Root (tenant-safe) */}
      <Route path="/" element={<FallbackRedirect replace />} />

      {/* 🧭 Tenant-safe fallback */}
      <Route path="*" element={<FallbackRedirect />} />
    </Routes>
  );
};

export default App;
